/**
 * AudioEngine — dual-deck Web Audio graph with crossfader, EQ, filter, reverb, compressor
 *
 * Graph per deck:
 *   DeckController._source → deckInput[id]
 *     → GainNode[vol]
 *     → BiquadFilter[low shelf]
 *     → BiquadFilter[mid peak]
 *     → BiquadFilter[high shelf]
 *     → BiquadFilter[filter knob]
 *     → Reverb.input → Reverb.output
 *     → AnalyserNode[viz]
 *     → GainNode[crossfade]
 *       ↘
 *         GainNode[master] → DynamicsCompressor → destination
 *       ↗
 *   (Deck B same chain)
 */

import { EffectsChain } from './effects.js'

export class AudioEngine {
  constructor(state) {
    this.ctx    = new (window.AudioContext || window.webkitAudioContext)()
    this.state  = state
    this.effects = new EffectsChain(this.ctx)
    this._workletReady = false

    // Master bus
    this.masterGain = this.ctx.createGain()
    this.masterGain.gain.value = 0.85
    this.compressor = this.effects.createMasterCompressor()
    this.masterGain.connect(this.compressor)
    this.compressor.connect(this.ctx.destination)

    // Build per-deck node chains
    this.deckNodes    = {}
    this.analysers    = {}
    this.crossfadeGains = {}

    for (const id of ['A', 'B']) {
      this._buildDeckChain(id)
    }

    this._crossfaderValue = 0.5
    this._applyCrossfader(0.5)

    // Load vocal isolation worklet (async, non-blocking)
    this._voxAvailable = false
    this._initVocalWorklet()
  }

  /**
   * Load the vocal isolation AudioWorklet module and insert nodes into each deck chain.
   * If loading fails (e.g. browser doesn't support AudioWorklet), vocal isolation
   * is simply unavailable — the rest of the app works fine.
   */
  async _initVocalWorklet() {
    try {
      const workletUrl = new URL('./vocal-worklet.js', import.meta.url)
      await this.ctx.audioWorklet.addModule(workletUrl)
      this._workletReady = true

      // Insert vocal isolator node into each deck chain (between vol and eqLow)
      for (const id of ['A', 'B']) {
        const nodes = this.deckNodes[id]
        const vocalNode = new AudioWorkletNode(this.ctx, 'vocal-isolator', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [2],
        })
        vocalNode.parameters.get('active').value = 0

        // Rewire: vol → vocalNode → eqLow (instead of vol → eqLow)
        nodes.vol.disconnect(nodes.eqLow)
        nodes.vol.connect(vocalNode)
        vocalNode.connect(nodes.eqLow)

        nodes.vocalIsolator = vocalNode
      }

      this._voxAvailable = true
      console.log('🎤 Vocal isolation worklet loaded')
    } catch (err) {
      console.warn('⚠️ Vocal isolation unavailable:', err.message)
      this._voxAvailable = false
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /** Decode an ArrayBuffer and return an AudioBuffer */
  async decodeAudio(arrayBuffer) {
    return this.ctx.decodeAudioData(arrayBuffer)
  }

  /** Detect BPM via onset-energy autocorrelation */
  async detectBPM(audioBuffer) {
    const data     = audioBuffer.getChannelData(0)
    const hopSize  = 512
    const frameRate = audioBuffer.sampleRate / hopSize

    // Energy envelope
    const energy = []
    for (let i = 0; i < data.length - hopSize; i += hopSize) {
      let e = 0
      for (let j = i; j < i + hopSize; j++) e += data[j] * data[j]
      energy.push(e)
    }

    // Onset strength (positive first derivative)
    const onset = []
    for (let i = 1; i < energy.length; i++) {
      onset.push(Math.max(0, energy[i] - energy[i - 1]))
    }

    // Autocorrelation sweep 60–180 BPM
    let bestCorr = -1, bestBPM = 128
    for (let bpm = 60; bpm <= 180; bpm++) {
      const lag = Math.round((60 / bpm) * frameRate)
      if (lag >= onset.length) continue
      let corr = 0
      const len = onset.length - lag
      for (let i = 0; i < len; i++) corr += onset[i] * onset[i + lag]
      if (corr > bestCorr) { bestCorr = corr; bestBPM = bpm }
    }
    return bestBPM
  }

  // ─── Volume & Crossfader ──────────────────────────────────────────────────

  setDeckVolume(id, value) {
    this.deckNodes[id].vol.gain.value = value
  }

  setMasterVolume(value) {
    this.masterGain.gain.value = value
  }

  /** value 0.0 (full A) → 0.5 (center) → 1.0 (full B) — equal-power law */
  setCrossfader(value) {
    value = Math.max(0, Math.min(1, value))
    this._crossfaderValue = value
    this._applyCrossfader(value)
  }

  // ─── EQ ───────────────────────────────────────────────────────────────────

  setEQ(id, band, db) {
    const nodes = this.deckNodes[id]
    if (band === 'low')       nodes.eqLow.gain.value  = db
    else if (band === 'mid')  nodes.eqMid.gain.value  = db
    else if (band === 'high') nodes.eqHigh.gain.value = db
    else console.warn('Invalid EQ band:', band)
  }

  /**
   * Kill (mute) an EQ band by slamming its gain to -40 dB
   * active = true → kill, false → restore
   */
  killBand(id, band, active) {
    const nodes = this.deckNodes[id]
    const db = active ? -40 : 0
    if (band === 'low')  nodes.eqLow.gain.value  = db
    if (band === 'mid')  nodes.eqMid.gain.value  = db
    if (band === 'high') nodes.eqHigh.gain.value = db
  }

  // ─── Vocal Isolation ──────────────────────────────────────────────────────

  /**
   * Toggle vocal isolation on/off for a deck
   * Returns the new state (true = vocals isolated, false = normal)
   */
  toggleVocalIsolation(id) {
    if (!this._voxAvailable) return false
    const nodes = this.deckNodes[id]
    if (!nodes.vocalIsolator || !this._workletReady) return false

    const param = nodes.vocalIsolator.parameters.get('active')
    const newState = param.value < 0.5 ? 1 : 0
    param.setValueAtTime(newState, this.ctx.currentTime)
    return newState > 0.5
  }

  /** Check if vocal isolation is currently active for a deck */
  isVocalIsolated(id) {
    const nodes = this.deckNodes[id]
    if (!nodes.vocalIsolator) return false
    return nodes.vocalIsolator.parameters.get('active').value > 0.5
  }

  // ─── Filter ───────────────────────────────────────────────────────────────

  setFilter(id, value) {
    this.deckNodes[id].filterCtrl.setValue(value)
  }

  // ─── Reverb ───────────────────────────────────────────────────────────────

  setReverb(id, wet) {
    this.deckNodes[id].reverb.setWet(wet)
  }

  // ─── Export ───────────────────────────────────────────────────────────────

  /**
   * Render the current mix offline.
   * Caller passes the two AudioBuffer objects and current deck states.
   */
  async exportMix(deckA, deckB) {
    if (!deckA.audioBuffer && !deckB.audioBuffer) {
      throw new Error('No tracks loaded — load at least one track before exporting.')
    }
    const duration = Math.max(
      deckA.audioBuffer ? deckA.audioBuffer.duration : 0,
      deckB.audioBuffer ? deckB.audioBuffer.duration : 0,
    )
    if (!duration) throw new Error('No tracks loaded')

    const sr      = this.ctx.sampleRate
    const offline = new OfflineAudioContext(2, Math.ceil(sr * duration), sr)
    const fx      = new EffectsChain(offline)
    const master  = offline.createGain()
    const comp    = fx.createMasterCompressor()
    master.connect(comp)
    comp.connect(offline.destination)
    master.gain.value = this.masterGain.gain.value

    const xf = this._crossfaderValue
    const gainA = Math.cos(xf * Math.PI / 2)
    const gainB = Math.cos((1 - xf) * Math.PI / 2)

    for (const [deck, cfGain] of [[deckA, gainA], [deckB, gainB]]) {
      if (!deck.audioBuffer) continue
      const src = offline.createBufferSource()
      src.buffer = deck.audioBuffer
      src.playbackRate.value = deck.playbackRate
      const g = offline.createGain()
      g.gain.value = cfGain
      src.connect(g)
      g.connect(master)
      src.start(0, deck.startOffset)
    }

    const rendered = await offline.startRendering()
    return this._bufferToWAV(rendered)
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  _buildDeckChain(id) {
    const c = this.ctx

    // Volume fader
    const vol = c.createGain()
    vol.gain.value = 1.0

    // 3-band EQ
    const eqLow = c.createBiquadFilter()
    eqLow.type = 'lowshelf'
    eqLow.frequency.value = 200
    eqLow.gain.value = 0

    const eqMid = c.createBiquadFilter()
    eqMid.type = 'peaking'
    eqMid.frequency.value = 1000
    eqMid.Q.value = 1
    eqMid.gain.value = 0

    const eqHigh = c.createBiquadFilter()
    eqHigh.type = 'highshelf'
    eqHigh.frequency.value = 5000
    eqHigh.gain.value = 0

    // Filter knob node
    const filterCtrl = this.effects.createFilter()

    // Reverb
    const reverb = this.effects.createReverb()

    // Analyser for waveform
    const analyser = c.createAnalyser()
    analyser.fftSize = 2048

    // Pre-crossfader gain
    const xfadeGain = c.createGain()

    // Wire the chain
    vol.connect(eqLow)
    eqLow.connect(eqMid)
    eqMid.connect(eqHigh)
    eqHigh.connect(filterCtrl.node)
    filterCtrl.node.connect(reverb.input)
    reverb.output.connect(analyser)
    analyser.connect(xfadeGain)
    xfadeGain.connect(this.masterGain)

    this.deckNodes[id] = {
      input: vol,   // DeckController sources connect here
      vol, eqLow, eqMid, eqHigh,
      filterCtrl, reverb, analyser,
    }
    this.analysers[id]     = analyser
    this.crossfadeGains[id] = xfadeGain
  }

  _applyCrossfader(x) {
    x = Math.max(0, Math.min(1, x))
    const gainA = Math.cos(x * Math.PI / 2)
    const gainB = Math.cos((1 - x) * Math.PI / 2)
    this.crossfadeGains['A'].gain.value = gainA
    this.crossfadeGains['B'].gain.value = gainB
  }

  _bufferToWAV(buffer) {
    const numCh   = buffer.numberOfChannels
    const sr      = buffer.sampleRate
    const len     = buffer.length
    const data    = new DataView(new ArrayBuffer(44 + len * numCh * 2))

    const write = (o, s) => {
      for (let i = 0; i < s.length; i++) data.setUint8(o + i, s.charCodeAt(i))
    }
    write(0,  'RIFF')
    data.setUint32(4,  36 + len * numCh * 2, true)
    write(8,  'WAVE')
    write(12, 'fmt ')
    data.setUint32(16, 16,       true)
    data.setUint16(20, 1,        true)
    data.setUint16(22, numCh,    true)
    data.setUint32(24, sr,       true)
    data.setUint32(28, sr * numCh * 2, true)
    data.setUint16(32, numCh * 2, true)
    data.setUint16(34, 16,       true)
    write(36, 'data')
    data.setUint32(40, len * numCh * 2, true)

    const channels = []
    for (let ch = 0; ch < numCh; ch++) channels.push(buffer.getChannelData(ch))

    let offset = 44
    for (let i = 0; i < len; i++) {
      for (let ch = 0; ch < numCh; ch++) {
        const s = Math.max(-1, Math.min(1, channels[ch][i]))
        data.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
        offset += 2
      }
    }

    return new Blob([data.buffer], { type: 'audio/wav' })
  }
}
